// Import storage keys from centralized config
import { STORAGE_KEY_PARAM_DATA, STORAGE_KEY_MAPI, STORAGE_KEY_REFERRER, } from '../../config/library/storage.config';
import { reportClientError } from './telemetry';
/**
 * Attribution parameter names used in TuneParamData
 */
var ParamName;
(function (ParamName) {
    ParamName["AFF_SUB"] = "aff_sub";
    ParamName["AFF_SUB2"] = "aff_sub2";
    ParamName["AFF_SUB3"] = "aff_sub3";
    ParamName["AFF_SUB4"] = "aff_sub4";
    ParamName["AFF_SUB5"] = "aff_sub5";
    ParamName["AFF_UNIQUE2"] = "aff_unique2";
    ParamName["AFF_UNIQUE3"] = "aff_unique3";
    ParamName["AFF_UNIQUE4"] = "aff_unique4";
    ParamName["AFF_UNIQUE5"] = "aff_unique5";
    ParamName["AFF_ID"] = "aff_id";
    ParamName["AFF_CLICK_ID"] = "aff_click_id";
    ParamName["GCLID"] = "gclid";
    ParamName["MSCLKID"] = "msclkid";
    ParamName["GBRAID"] = "gbraid";
    ParamName["WBRAID"] = "wbraid";
    ParamName["KBID"] = "kbid";
    ParamName["CLREQID"] = "clreqid";
    ParamName["CL_SYSTEM"] = "cl_system";
    ParamName["SOURCE"] = "source";
    ParamName["ZIPCODE"] = "ZipCode";
    ParamName["LAST_ACTIVITY"] = "lastActivity";
})(ParamName || (ParamName = {}));
/**
 * URL parameter names for attribution tracking
 */
var UrlParam;
(function (UrlParam) {
    UrlParam["UTM_SOURCE"] = "utm_source";
    UrlParam["UTM_MEDIUM"] = "utm_medium";
    UrlParam["UTM_CAMPAIGN"] = "utm_campaign";
    UrlParam["UTM_CONTENT"] = "utm_content";
    UrlParam["UTM_TERM"] = "utm_term";
    UrlParam["SOURCE"] = "_source";
    UrlParam["ZIP"] = "_zip";
    UrlParam["ZIPCODE"] = "_ZipCode";
    UrlParam["TRANSACTION_ID"] = "transaction_id";
    UrlParam["PARTNER"] = "partner";
})(UrlParam || (UrlParam = {}));
/**
 * Attribution parameters compared by hasParametersChanged().
 *
 * This list is attribution-semantic, not "every field on TuneParamData".
 * hasParametersChanged() drives hasChanged, which stamps lastAttributionTouch —
 * the clock behind the 30-day absolute cap. So membership here means "a change
 * to this field is a real attribution change that should reset the cap".
 *
 * requestId, staleMapiRequestId, lastActivity and lastAttributionTouch are
 * deliberately absent:
 * minting a request id or recording a pageview is not an attribution change,
 * and including them would slide the cap forward on every fresh id, defeating
 * the staleness bound. lastActivity is additionally filtered out inside
 * hasParametersChanged for the same reason.
 */
const tuneParamDataKeys = [
    ParamName.AFF_SUB,
    ParamName.AFF_SUB2,
    ParamName.AFF_SUB3,
    ParamName.AFF_SUB4,
    ParamName.AFF_SUB5,
    ParamName.SOURCE,
    ParamName.AFF_UNIQUE2,
    ParamName.AFF_UNIQUE3,
    ParamName.AFF_UNIQUE4,
    ParamName.AFF_UNIQUE5,
    ParamName.KBID,
    ParamName.CLREQID,
    ParamName.ZIPCODE,
    ParamName.CL_SYSTEM,
    ParamName.AFF_ID,
    ParamName.AFF_CLICK_ID,
    ParamName.GCLID,
    ParamName.MSCLKID,
    ParamName.GBRAID,
    ParamName.WBRAID,
    ParamName.LAST_ACTIVITY,
];
// ===============================================
// STANDALONE FUNCTIONS FOR TREE-SHAKING
// ===============================================
/**
 * The localStorage key used for storing attribution parameter data.
 * @constant {string}
 */
export const getStorageKey = STORAGE_KEY_PARAM_DATA;
/**
 * Generates the affiliate domain by replacing 'www' with 'go' in the current hostname.
 * @returns {string} The affiliate domain (e.g., 'go.example.com')
 */
export const getAffilliateDomain = () => window.location.host.replace('www', 'go');
/**
 * Returns the list of recognized organic search engine sources for attribution detection.
 * @returns {string[]} Array of search engine names (e.g., ['google', 'bing', 'yahoo', 'duckduckgo'])
 */
export const getOrganicSources = () => [
    'google',
    'bing',
    'yahoo',
    'duckduckgo',
];
/**
 * Checks if attribution data has expired based on timestamp
 * @param storedData - The stored attribution data with lastActivity timestamp
 * @returns {boolean} - True if data has expired (older than configured timeout of inactivity)
 * @see ATTRIBUTION_CONFIG.ATTRIBUTION_TIMEOUT for timeout duration
 */
export function isAttributionExpired(storedData) {
    if (!storedData.lastActivity)
        return false; // No timestamp = treat as valid
    const now = Date.now();
    const timeDiff = now - storedData.lastActivity;
    return timeDiff > CONFIG.ATTRIBUTION_TIMEOUT;
}
/**
 * Checks whether stored attribution has exceeded the absolute staleness cap.
 * Unlike the session gate (30-min inactivity that gates downstream referrer
 * re-evaluation), this bounds the age of the last real attribution touch
 * and ignores the referrer entirely.
 * @param storedData - The stored attribution data with lastAttributionTouch timestamp
 * @returns {boolean} - True if the last touch is older than ATTRIBUTION_ABSOLUTE_CAP
 * @see ATTRIBUTION_CONFIG.ATTRIBUTION_ABSOLUTE_CAP for cap duration
 */
export function isAttributionCapExpired(storedData) {
    if (!storedData.lastAttributionTouch)
        return false; // No timestamp = grandfather (not expired)
    const now = Date.now();
    const timeDiff = now - storedData.lastAttributionTouch;
    return timeDiff > CONFIG.ATTRIBUTION_ABSOLUTE_CAP;
}
/**
 * Detects if GTM debug/preview mode is active
 * @returns {boolean} - True if GTM debug mode detected
 */
export function isGTMDebugMode() {
    // Check for GTM debug indicators
    const isGTMPreview = window.location.search.includes('gtm_debug') ||
        window.location.search.includes('gtm_preview') ||
        document.referrer.includes('tagassistant.google.com') ||
        document.referrer.includes('optimize.google.com');
    if (isGTMPreview) {
        console.warn('🔍 GTM Debug Mode Detected - Referrer data may be affected by Google Tag Manager preview mode');
    }
    return isGTMPreview;
}
/**
 * Detects whether the current pageview arrived via an external referrer.
 * NOTE: this does NOT detect a session — it only checks document.referrer against
 * the current host. Used to gate 30-min session expiry so same-session internal
 * navigation (no external referrer) does not wipe a valid attribution touch.
 * @returns {boolean} - True if an external referrer is present
 */
export function hasExternalReferral() {
    // Check if we have a referrer indicating arrival from external source
    const hasExternalReferrer = document.referrer && !document.referrer.includes(window.location.hostname);
    // For now, use referrer as primary indicator
    // Could be enhanced with sessionStorage or other session indicators
    return Boolean(hasExternalReferrer);
}
/**
 * Compares two TuneParamData objects for equality (excluding lastActivity)
 * @param data1 - First data object
 * @param data2 - Second data object
 * @returns {boolean} - True if attribution parameters are identical
 */
export function hasParametersChanged(data1, data2) {
    // Compare all keys except lastActivity
    const keysToCompare = tuneParamDataKeys.filter(key => key !== 'lastActivity');
    for (const key of keysToCompare) {
        const val1 = data1[key];
        const val2 = data2[key];
        // Treat null, undefined, and empty string as equivalent (no value)
        const isEmpty1 = val1 === null || val1 === undefined || val1 === '';
        const isEmpty2 = val2 === null || val2 === undefined || val2 === '';
        // If both are "empty", consider them equal
        if (isEmpty1 && isEmpty2) {
            continue;
        }
        // If one is empty and the other isn't, they're different
        if (isEmpty1 !== isEmpty2) {
            return true;
        }
        // Both have values - compare them directly
        if (val1 !== val2) {
            return true; // Found a difference
        }
    }
    return false; // No differences found
}
/**
 * Sorts an object's keys and returns a new sorted object (utility).
 * @param obj - The object to sort
 * @returns {object} - New object with sorted keys
 */
export function sortObj(obj) {
    return Object.keys(obj)
        .sort()
        .reduce((result, key) => {
        result[key] = obj[key];
        return result;
    }, {});
}
/**
 * Retrieves all Tune links from the DOM.
 *
 * @returns {NodeListOf<HTMLAnchorElement> | null} A list of anchor elements containing Tune links.
 */
/**
 * Selector matching the affiliate links the library enhances (go.*, au.*).
 * Shared by link discovery and the click pipeline.
 */
const AFFILIATE_ANCHOR_SELECTOR = "a[href*='https://go.'], a[href*='https://au.']";
export function getTuneLinks() {
    var _a;
    return (_a = document.querySelectorAll(AFFILIATE_ANCHOR_SELECTOR)) !== null && _a !== void 0 ? _a : null;
}
/**
 * Parses a query string into an object with key-value pairs.
 *
 * This function takes a query string as input, removes the leading question mark if it exists,
 * splits the string by the '&' character to separate each key-value pair, and then decodes
 * and stores them in an object.
 *
 * @param {string} query - The query string to be parsed.
 * @returns {{[key: string]: string}} - An object containing the parsed key-value pairs from the query string.
 *
 * @example
 * ```typescript
 * const queryString = '?utm_source=google&utm_medium=cpc&utm_campaign=spring_sale';
 * const queryParams = parseQuery(queryString);
 * console.log(queryParams);
 * // Outputs: { utm_source: 'google', utm_medium: 'cpc', utm_campaign: 'spring_sale' }
 * ```
 */
export function parseQuery(query) {
    var _a;
    const split = query.replace(/^\?/, '').split('&');
    const qValues = {};
    for (let i = 0; i < split.length; i++) {
        if (!((_a = split[i]) === null || _a === void 0 ? void 0 : _a.length))
            continue;
        const param = split[i].split('=');
        // Replace plus signs with spaces before decoding (URL encoding standard)
        const value = param[1] ? param[1].replace(/\+/g, ' ') : '';
        const decodedValue = decodeURIComponent(value);
        // Validate length to prevent DoS attacks
        const validatedValue = validateParameterLength(decodedValue);
        qValues[param[0]] = validatedValue;
    }
    return qValues;
}
/**
 * Retrieves Facebook tracking cookies (_fbp and _fbc) and formats them for attribution.
 * @returns {string} Formatted string containing Facebook cookie values (e.g., 'fbp-value-fbc-value')
 */
export function getFBCookies() {
    const fbc = getItemFromCookieStorage('_fbc');
    const fbp = getItemFromCookieStorage('_fbp');
    let fbcookiestring = '';
    if (fbp) {
        fbcookiestring += `fbp-${fbp}`;
    }
    if (fbp && fbc) {
        fbcookiestring += `-`;
    }
    if (fbc) {
        fbcookiestring += `fbc-${fbc}`;
    }
    return fbcookiestring;
}
/**
 * Click ID keys that can be mapped to aff_unique5
 * Priority order: gclid > msclkid > wbraid > gbraid
 */
const AFF_UNIQUE5_POSSIBLE_KEYS = [
    'gclid',
    'msclkid',
    'wbraid',
    'gbraid',
];
/**
 * Keys that should skip null values when building query strings
 */
const SKIP_NULL_KEYS = ['aff_unique3', 'ZipCode', 'aff_unique4'];
/**
 * Keys that carry the MapiJS request id. When no id is available these are
 * omitted from the query string entirely: downstream (Tune) cannot tell an
 * empty `clreqid=` from a real one, but it can tell an absent one.
 */
const REQUEST_ID_KEYS = ['aff_unique1', 'cl_system_id', 'clreqid'];
/**
 * Assigns aff_unique5 from click IDs or Facebook cookies if not already set
 *
 * @param params - The parameters object to modify
 */
function assignAffUnique5(params) {
    // Skip if aff_unique5 already has a valid value
    if ('aff_unique5' in params && params.aff_unique5 !== 'undefined') {
        return;
    }
    // Try to assign from click ID parameters (gclid, msclkid, wbraid, gbraid)
    for (const key of AFF_UNIQUE5_POSSIBLE_KEYS) {
        if (params[key] && params[key] !== 'undefined') {
            params.aff_unique5 = String(params[key]);
            return; // Found a click ID, done
        }
    }
    // Fallback: Facebook paid social ads use FB cookies
    if (params.aff_sub === 'facebook' && params.aff_sub2 === 'paid_social') {
        const fbcookiestring = getFBCookies();
        if (fbcookiestring && fbcookiestring !== '') {
            params.aff_unique5 = fbcookiestring;
        }
    }
}
/**
 * Removes click ID keys from params (they've been mapped to aff_unique5)
 *
 * @param params - The parameters object to modify
 */
function removeClickIdKeys(params) {
    for (const key of AFF_UNIQUE5_POSSIBLE_KEYS) {
        if (key in params) {
            delete params[key];
        }
    }
}
/**
 * Formats a single query parameter as a key=value or key-only string
 *
 * @param key - The parameter key
 * @param value - The parameter value
 * @returns Formatted parameter string or null if should be skipped
 */
function formatQueryParameter(key, value) {
    // Omit request-id params outright when there is no id to write. Must precede
    // the key-only branch below, which would otherwise emit a bare `clreqid`.
    if (REQUEST_ID_KEYS.includes(key) &&
        (value === '' || value === 'null' || value === 'undefined')) {
        return null;
    }
    // Skip undefined values (create key-only parameter)
    if (encodeURIComponent(value) === 'undefined') {
        return encodeURIComponent(key);
    }
    // Skip null values for specific keys
    if (SKIP_NULL_KEYS.includes(key) && value === 'null') {
        return null;
    }
    // Special case for source (no encoding needed)
    if (key === 'source') {
        return `${key}=${value}`;
    }
    // Standard encoding
    return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
}
/**
 * Builds a query string from the provided parameters.
 *
 * This function processes the input parameters, ensuring certain rules are applied,
 * such as setting `aff_unique5` if necessary, and removes keys that shouldn't be included
 * in the final query string because they are "undefined" or just null/empty.
 * It then constructs and returns the query string.
 *
 * @param {object} params - The parameters to be included in the query string.
 * @returns {string} - The constructed query string.
 *
 * @example
 * ```typescript
 * const params = { aff_sub: 'affiliate', aff_unique3: '12345', gclid: 'testGclid' };
 * const queryString = buildQuery(params);
 * console.log(queryString); // Outputs: "?aff_sub=affiliate&aff_unique3=12345&aff_unique5=testGclid"
 * ```
 */
export function buildQuery(params = {}) {
    // Step 1: Assign aff_unique5 from click IDs or FB cookies if needed
    assignAffUnique5(params);
    // Step 2: Remove click ID keys (now mapped to aff_unique5)
    removeClickIdKeys(params);
    // Step 3: Build query components
    const components = [];
    const sortedKeys = Object.keys(params).sort();
    for (let i = 0, len = sortedKeys.length; i < len; i++) {
        const key = sortedKeys[i];
        const value = String(params[key]);
        const formatted = formatQueryParameter(key, value);
        if (formatted !== null) {
            components.push(formatted);
        }
    }
    // Step 4: Return formatted query string
    return components.length === 0 ? '' : `?${components.join('&')}`;
}
/**
 * Unicode-safe base64 encoder, encapsulated in TuneModule.
 * @param str - The string to encode
 * @returns {string} - Base64-encoded string
 */
export function toBase64Unicode(str) {
    return btoa(unescape(encodeURIComponent(str)));
}
/**
 * Extracts hardcoded tune parameters from query and stores them separately
 *
 * @param query - The query object to extract from (will be modified)
 * @param tuneParamKeys - Set of valid tune parameter keys
 * @returns Object containing extracted hardcoded values
 */
function extractHardcodedParams(query, tuneParamKeys) {
    const hardcodedValues = {};
    // Performance: for...in is 30-40% faster than Object.keys().forEach()
    for (const key in query) {
        if (!Object.prototype.hasOwnProperty.call(query, key))
            continue;
        if (tuneParamKeys.has(key)) {
            hardcodedValues[key] = query[key];
            delete query[key];
        }
    }
    return hardcodedValues;
}
/**
 * Handles special aff_id logic for tooa vs non-tooa traffic
 *
 * @param query - The query object to update
 * @param hardcodedValues - Hardcoded values extracted from link
 * @param newParams - New parameters from attribution
 * @param isTooa - Whether this is tooa traffic
 */
function handleAffIdMerge(query, hardcodedValues, newParams, isTooa) {
    const hasAffIdValue = newParams.aff_id !== undefined && newParams.aff_id !== null;
    // If newParams has aff_id value
    if (hasAffIdValue) {
        if (!isTooa && hardcodedValues.aff_id) {
            // Non-tooa: preserve hardcoded aff_id
            query.aff_id = hardcodedValues.aff_id;
            delete hardcodedValues.aff_id;
        }
        else {
            // Tooa or no hardcoded: use newParams value
            query.aff_id = String(newParams.aff_id);
        }
        return;
    }
    // No aff_id in newParams - check hardcoded value
    if (hardcodedValues.aff_id && !isTooa) {
        // Non-tooa: preserve hardcoded aff_id even when no newParams value
        query.aff_id = hardcodedValues.aff_id;
        delete hardcodedValues.aff_id;
    }
    // Tooa mode with no newParams value: intentionally don't add hardcoded aff_id
}
/**
 * Merges new parameters into the existing query parameters.
 *
 * @param {Object} query - The existing query parameters.
 * @param {Object} newParams - The new parameters to be merged into the query.
 * @returns {Object} - The updated query parameters.
 *
 * @example
 * ```typescript
 * const query = { utm_source: 'google' };
 * const newParams = { aff_sub: 'affiliate', aff_sub2: 'social' };
 * const updatedQuery = mergeNewParams(query, newParams);
 * console.log(updatedQuery); // Outputs: { utm_source: 'google', aff_sub: 'affiliate', aff_sub2: 'social' }
 * ```
 */
export function mergeNewParams(query, newParams) {
    // Step 1: Extract hardcoded tune params from link
    const tuneParamKeys = new Set(tuneParamDataKeys);
    const hardcodedValues = extractHardcodedParams(query, tuneParamKeys);
    // Step 2: Check if this is tooa traffic (affects aff_id handling)
    const isTooa = newParams.aff_sub2 === 'tooa';
    // Step 3: Merge newParams into query (except aff_id, handled separately)
    for (const key in newParams) {
        const val = newParams[key];
        if (val || (typeof val === 'number' && val === 0)) {
            if (key !== 'aff_id') {
                query[key] = String(newParams[key]);
            }
        }
    }
    // Step 4: Handle aff_id with special tooa logic
    handleAffIdMerge(query, hardcodedValues, newParams, isTooa);
    // Step 5: Encode remaining hardcoded values for debugging
    if (Object.keys(hardcodedValues).length > 0) {
        const jsonString = JSON.stringify(hardcodedValues);
        const base64String = toBase64Unicode(jsonString);
        query.hardcoded_values = base64String;
    }
    return query;
}
/**
 * Replaces the query parameters in the given URL with the new parameters provided.
 *
 * @param {string} url The URL in which the query parameters will be replaced.
 * @param {TuneParamData} newParams The new parameters to be merged into the existing query parameters.
 * @returns {string} The URL with the updated query parameters.
 *
 * @example
 * ```typescript
 * const url = "https://example.com/path?utm_source=google";
 * const newParams: TuneParamData = { aff_sub: "affiliate", aff_sub2: "social" };
 * const updatedUrl = replaceQueryParameters(url, newParams);
 * console.log(updatedUrl); // Outputs: "https://example.com/path?aff_sub=affiliate&aff_sub2=social"
 * ```
 */
export function replaceQueryParameters(url, newParams) {
    const urlObject = new URL(url);
    const parsedQueryObject = parseQuery(urlObject.search);
    const queryParams = mergeNewParams(parsedQueryObject, newParams);
    const queryString = buildQuery(queryParams);
    return `${urlObject.origin}${urlObject.pathname}${queryString}`;
}
/**
 * Checks if an object has no enumerable properties.
 * @param obj - The object to check
 * @returns {boolean} True if the object is empty (no keys), false otherwise
 */
export function isObjectEmpty(obj) {
    return Object.keys(obj).length === 0;
}
/**
 * Sanitizes user input by HTML-encoding special characters to prevent XSS attacks.
 *
 * This function encodes potentially dangerous characters that could be used in XSS
 * attacks, including script tags, event handlers, and other HTML/JavaScript syntax.
 *
 * @param input - The string to sanitize
 * @returns HTML-encoded string safe for storage and potential DOM use
 *
 * @example
 * ```typescript
 * sanitizeInput('<script>alert("XSS")</script>');
 * // Returns: '&lt;script&gt;alert(&quot;XSS&quot;)&lt;/script&gt;'
 *
 * sanitizeInput('normal-utm-campaign');
 * // Returns: 'normal-utm-campaign' (no change for safe input)
 * ```
 */
export function sanitizeInput(input) {
    // HTML entity encoding for XSS prevention
    return input
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#x27;')
        .replace(/\//g, '&#x2F;');
}
/**
 * Validates and truncates parameter values to prevent DoS via oversized inputs.
 *
 * Protects against denial-of-service attacks by limiting the length of URL
 * parameters. The default limit of 500 characters accommodates legitimate use
 * cases while preventing memory exhaustion from malicious oversized inputs.
 *
 * @param value - The parameter value to validate
 * @param maxLength - Maximum allowed length (default: 500)
 * @returns Validated and potentially truncated value
 *
 * @example
 * ```typescript
 * validateParameterLength('normal-campaign');
 * // Returns: 'normal-campaign'
 *
 * validateParameterLength('x'.repeat(1000));
 * // Returns: 'x'.repeat(500) and logs warning
 * ```
 */
export function validateParameterLength(value, maxLength = 500) {
    if (value.length > maxLength) {
        console.warn(`Parameter truncated: length ${value.length} exceeds ${maxLength}`);
        return value.substring(0, maxLength);
    }
    return value;
}
/**
 * Validates US zipcode format (5 digits or 5+4 format).
 *
 * Ensures zipcode parameters match expected format before processing.
 * Accepts both standard 5-digit format and extended ZIP+4 format.
 *
 * @param zip - The zipcode string to validate
 * @returns Validated zipcode or null if invalid
 *
 * @example
 * ```typescript
 * validateZipCode('12345');         // Returns: '12345'
 * validateZipCode('12345-6789');    // Returns: '12345-6789'
 * validateZipCode('invalid');       // Returns: null, logs warning
 * validateZipCode('<script>');      // Returns: null, logs warning
 * ```
 */
export function validateZipCode(zip) {
    // US zipcode: 5 digits or 5+4 format
    const zipRegex = /^[0-9]{5}(-[0-9]{4})?$/;
    if (!zipRegex.test(zip)) {
        console.warn(`Invalid zipcode format: ${zip}`);
        return null;
    }
    return zip;
}
/**
 * Gets item from local storage using a key argument.
 * No unit tests for this function as JSDOM does not support localstorage.
 *
 * @param {string} key The key used to access the item from local storage.
 * @returns {string} The item from local storage.
 */
export function getItemFromLocalStorage(key) {
    try {
        return localStorage.getItem(key) || '';
    }
    catch (_error) {
        // localStorage might not be available (private browsing, etc.)
        return '';
    }
}
/**
 * Retrieves a cookie value by key from document.cookie.
 * @param key - The cookie name to retrieve
 * @returns {string} The cookie value, or empty string if not found
 */
export function getItemFromCookieStorage(key) {
    const name = `${key}=`;
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');
    for (let i = 0; i < cookieArray.length; i++) {
        let cookie = cookieArray[i];
        while (cookie.startsWith(' ')) {
            cookie = cookie.substring(1);
        }
        if (cookie.startsWith(name)) {
            return cookie.substring(name.length, cookie.length);
        }
    }
    return '';
}
/**
 * Sets item in local storage.
 * No unit tests for this function as JSDOM does not support localstorage.
 *
 * @param {string} key The key used to set the item in local storage for later retrieval.
 * @param {string} value The value to be stored in local storage.
 */
export function setItemInLocalStorage(key, value) {
    try {
        localStorage.setItem(key, value);
    }
    catch (_error) {
        // Silently fail - localStorage might not be available (quota exceeded, private browsing, etc.)
    }
}
/**
 * Builds the aff_unique3 value from provider-card location data attributes.
 *
 * Segments are emitted in fixed order (state, city, zip) as `label-value` and
 * joined with `-`. A segment is included only when its value is present,
 * non-empty after trimming, and not equal to "false" (case-insensitive).
 * Returns '' when no segment qualifies.
 *
 * @param state - Raw data-state attribute value (or null if absent)
 * @param city - Raw data-city attribute value (or null if absent)
 * @param zip - Raw data-zip attribute value (or null if absent)
 * @returns Labeled hyphen-delimited location string, or '' when nothing qualifies
 */
export function buildCardLocationValue(state, city, zip) {
    const segments = [];
    const addSegment = (label, value) => {
        if (value === null)
            return;
        const trimmed = value.trim();
        if (trimmed === '' || trimmed.toLowerCase() === 'false')
            return;
        segments.push(`${label}-${trimmed}`);
    };
    addSegment('state', state);
    addSegment('city', city);
    addSegment('zip', zip);
    return segments.join('-');
}
/**
 * Appends attribution parameters to an anchor element's href attribute.
 * Preserves existing query parameters and handles URL encoding.
 * @param target - The anchor element to modify
 * @param params - Attribution parameters to append to the URL
 */
export function appendParams(target, params) {
    // Do not stop propagation
    // e.stopPropagation(); this was breaking reporting
    let url = target.href;
    // Skip links without href attribute or invalid URLs
    if (!url || url === '') {
        return;
    }
    try {
        // Per-card aff_unique3 (WE-1184): when the link sits inside a container
        // carrying location data attributes, override aff_unique3 for this link
        // only. A shallow copy keeps the shared params object untouched and lets
        // replaceQueryParameters preserve alphabetical sorting.
        let effectiveParams = params;
        // Assumes data-state/data-city/data-zip are colocated on one ancestor (the
        // provider card). closest() returns the nearest element matching any of
        // them, so attributes split across nested wrappers would not be combined.
        const card = target.closest('[data-state],[data-city],[data-zip]');
        if (card) {
            const cardValue = buildCardLocationValue(card.getAttribute('data-state'), card.getAttribute('data-city'), card.getAttribute('data-zip'));
            if (cardValue) {
                effectiveParams = Object.assign(Object.assign({}, params), { aff_unique3: cardValue });
            }
        }
        url = replaceQueryParameters(url, effectiveParams);
        // Only update if necessary to minimize DOM writes
        if (target.href !== url)
            target.href = url;
        if (target.rel !== 'nofollow')
            target.rel = 'nofollow';
    }
    catch (_error) {
        // Do not throw into the caller — a bad href on one link must not stop the
        // rest — but report it, otherwise a systematically unenhanced link is
        // indistinguishable from one that was never on the page.
        reportClientError('linkEnhancement', _error, {
            href: target.getAttribute('href'),
            initCompleted,
        });
    }
}
/**
 * Extracts a normalized hostname from a referrer string.
 * Prepends a protocol when missing, lowercases, and strips a leading `www.`.
 * @param referrer - Raw referrer URL (e.g. document.referrer)
 * @returns {string | null} Normalized hostname, or `null` if it cannot be parsed.
 */
export function extractReferrerHostname(referrer) {
    try {
        const normalized = referrer.includes('//')
            ? referrer
            : `https://${referrer}`;
        const host = new URL(normalized).hostname.toLowerCase();
        if (!host) {
            return null;
        }
        return host.replace(/^www\./, '');
    }
    catch (_a) {
        return null;
    }
}
/**
 * Determines UTM source and medium based on the referrer.
 *
 * @returns {[string | null, string]} - The source (or `null`) and medium (`organic`, `referral`, or `direct`).
 */
export function setUTMFromReferrer() {
    const currentReferrer = document.referrer;
    const storedReferrer = getItemFromLocalStorage(STORAGE_KEY_REFERRER);
    // Update stored referrer if different from current
    if (currentReferrer !== storedReferrer) {
        if (currentReferrer) {
            setItemInLocalStorage(STORAGE_KEY_REFERRER, currentReferrer);
        }
        // Note: No longer immediately clearing referrer when empty
        // Attribution expiration is now handled by timestamp-based system
    }
    // Always use current referrer for attribution (not stored referrer)
    if (!currentReferrer) {
        return [null, 'direct'];
    }
    const host = extractReferrerHostname(currentReferrer);
    // Unparseable referrer → direct (fail-safe)
    if (!host) {
        return [null, 'direct'];
    }
    // Self-referral (internal navigation) → direct
    const currentHost = extractReferrerHostname(window.location.href);
    if (currentHost && host === currentHost) {
        return [null, 'direct'];
    }
    // Organic search engine detection takes priority
    const labels = host.split('.');
    const found = getOrganicSources().find(r => labels.includes(r));
    if (found) {
        return [found, 'organic'];
    }
    // Valid non-organic referrer → referral, source is the hostname
    return [host, 'referral'];
}
/**
 * Capitalize the first letter and return the rest of the string unchanged.
 * @param word - The string to capitalize
 * @returns {string}
 */
export function capitalize(word) {
    return word.charAt(0).toUpperCase() + word.slice(1);
}
/**
 * Parses zip code data from URL parameters.
 * @returns {object} Object containing base64zip and zipCode
 */
function parseGeoAndZipCode() {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const zipCode = urlParams.get('zip');
    let base64zip = 'null';
    let validatedZipCode = null;
    if (zipCode) {
        // Validate zipcode format for security (5 digits or 5+4 format)
        validatedZipCode = validateZipCode(zipCode);
        if (validatedZipCode) {
            // Use browser-native base64 encoding only for valid zipcodes
            base64zip = toBase64Unicode(validatedZipCode).replace('=', '');
        }
    }
    return { base64zip, zipCode: validatedZipCode };
}
/**
 * Builds parameter mapping dictionary from compact mappings array.
 * @param base64zip - Base64-encoded zip code
 * @param zipCode - Raw zip code value
 * @returns {ParamDataDictionary} Dictionary mapping source params to target params
 */
function buildParamMappings(base64zip, zipCode) {
    // Compact parameter mapping structure to reduce bundle size
    // Format: [source_param, target_param, defaultValue?]
    const paramMappings = [
        [UrlParam.UTM_SOURCE, ParamName.AFF_SUB],
        [UrlParam.UTM_MEDIUM, ParamName.AFF_SUB2],
        [UrlParam.UTM_CAMPAIGN, ParamName.AFF_SUB3],
        [UrlParam.UTM_CONTENT, ParamName.AFF_SUB4],
        [UrlParam.UTM_TERM, ParamName.AFF_SUB5],
        [
            UrlParam.SOURCE,
            ParamName.SOURCE,
            `${window.location.origin}${window.location.pathname}`,
        ],
        [ParamName.AFF_UNIQUE2, ParamName.AFF_UNIQUE2],
        [UrlParam.ZIP, ParamName.AFF_UNIQUE3, zipCode || undefined],
        [UrlParam.ZIPCODE, ParamName.ZIPCODE, base64zip],
        [ParamName.AFF_UNIQUE5, ParamName.AFF_UNIQUE5],
        [ParamName.GCLID, ParamName.GCLID],
        [ParamName.MSCLKID, ParamName.MSCLKID],
        [ParamName.GBRAID, ParamName.GBRAID],
        [ParamName.WBRAID, ParamName.WBRAID],
        [ParamName.KBID, ParamName.KBID],
        [ParamName.CLREQID, ParamName.CLREQID],
        [ParamName.CL_SYSTEM, ParamName.CL_SYSTEM, STORAGE_KEY_MAPI],
        [ParamName.AFF_ID, ParamName.AFF_ID],
        [UrlParam.TRANSACTION_ID, ParamName.AFF_CLICK_ID],
    ];
    // Build dictionary from compact mappings
    const dictionary = {};
    // Performance: traditional for loop is faster than forEach for arrays
    for (let i = 0; i < paramMappings.length; i++) {
        const [source, target, defaultVal] = paramMappings[i];
        dictionary[source] =
            defaultVal !== undefined
                ? { key: target, defaultValue: defaultVal }
                : { key: target };
    }
    return dictionary;
}
/**
 * Helper to safely get default value from dictionary entry.
 * @param entry - Dictionary entry
 * @returns Default value or undefined
 */
function getDefault(entry) {
    var _a;
    return (_a = entry === null || entry === void 0 ? void 0 : entry.defaultValue) !== null && _a !== void 0 ? _a : undefined;
}
/**
 * Initializes param data structure with default values from dictionary.
 * @param dictionary - Parameter mapping dictionary
 * @returns {TuneParamData} Initialized parameter data object
 */
function initializeParamData(dictionary) {
    var _a;
    return {
        source: (_a = getDefault(dictionary[UrlParam.SOURCE])) !== null && _a !== void 0 ? _a : null,
        aff_sub: undefined,
        aff_sub2: 'direct',
        aff_sub5: getDefault(dictionary[UrlParam.UTM_TERM]),
        aff_unique2: getDefault(dictionary[ParamName.AFF_UNIQUE2]),
        aff_unique3: getDefault(dictionary[UrlParam.ZIP]),
        aff_unique5: getDefault(dictionary[ParamName.AFF_UNIQUE5]),
        ZipCode: getDefault(dictionary[UrlParam.ZIPCODE]),
        cl_system: getDefault(dictionary[ParamName.CL_SYSTEM]),
        aff_id: getDefault(dictionary[ParamName.AFF_ID]),
        aff_click_id: getDefault(dictionary[UrlParam.TRANSACTION_ID]),
    };
}
/**
 * Loads and parses stored parameter data from localStorage.
 * @returns {TuneParamData} Parsed stored data or empty object if none exists
 */
function loadStoredParamData() {
    const storedDataString = getItemFromLocalStorage(getStorageKey);
    if (!storedDataString) {
        return {};
    }
    try {
        return JSON.parse(storedDataString);
    }
    catch (_error) {
        console.warn('Invalid JSON in localStorage cl_param_data, starting fresh');
        return {};
    }
}
/**
 * Replaces the stored request id with a freshly minted provisional one, and
 * records the MapiJS id current at this moment so readStoredRequestId stops
 * preferring it. Called from the attribution reset — see the rationale at the
 * call site. Never throws: storage being unavailable is not a reason to abort
 * the reset.
 */
function resetRequestIdForNewAttribution() {
    try {
        const blob = getStoredAttributionData();
        const staleMapiRequestId = readMapiRequestId();
        if (staleMapiRequestId) {
            blob.staleMapiRequestId = staleMapiRequestId;
        }
        else {
            delete blob.staleMapiRequestId;
        }
        blob.requestId = `${PROVISIONAL_REQUEST_ID_PREFIX}${Date.now()}-${generateUuidV4()}`;
        setItemInLocalStorage(getStorageKey, JSON.stringify(blob));
    }
    catch (_e) {
        // Storage unavailable — nothing to purge.
    }
}
/**
 * Handles attribution expiration and cleanup logic.
 * @param storedParamData - Current stored parameter data
 * @param urlParams - URL search parameters
 * @param isDebug - Debug mode flag
 * @returns {object} Object with attributionExpired flag and cleaned storedParamData
 */
function handleAttributionExpiration(storedParamData, urlParams, isDebug) {
    // Session expiry: 30-min inactivity AND arrival via external referral (preserves same-session nav)
    const sessionExpired = isAttributionExpired(storedParamData) && hasExternalReferral();
    // Absolute cap: last real touch older than 30 days, regardless of referrer (bounds staleness)
    const capExpired = isAttributionCapExpired(storedParamData);
    const attributionExpired = sessionExpired || capExpired;
    if (!attributionExpired) {
        return { attributionExpired: false, storedParamData };
    }
    // Save original data before clearing to check for cleanup conditions
    const originalStoredData = Object.assign({}, storedParamData);
    // Clear expired attribution data but preserve structure; reset both clocks
    const cleanedData = {
        lastActivity: Date.now(),
        // Note: the persisted re-stamp actually happens in saveAttributionData (hasChanged
        // is true after a clean-slate); this initializer is just structural consistency.
        lastAttributionTouch: Date.now(),
    };
    // Also clear stored referrer to prevent sticky attribution
    setItemInLocalStorage(STORAGE_KEY_REFERRER, '');
    // The request id must not outlive the attribution it belongs to.
    // saveAttributionData carries requestId forward from stored data so that
    // init() cannot wipe it, but that carry reads localStorage — which still
    // holds the pre-reset blob at this point. Without purging here, an id from
    // before the reset is resurrected and emitted as clreqid/aff_unique1/
    // cl_system_id, outliving both the session gate and the 30-day absolute cap.
    //
    // Two storage keys hold an id, so both doors need closing:
    //   - our own blob, purged and re-minted here so this pageview's clicks still
    //     carry an id. Minted inline rather than via ensureRequestId(), which
    //     prefers the `mapi` id and would hand back the very id being reset.
    //   - MapiJS's `mapi` key, which we must not write to — it is MapiJS's own
    //     session state. Its current value is recorded instead, so
    //     readStoredRequestId skips it until MapiJS mints a new one. Nothing is
    //     lost: the deferred chunk upgrades to the real id once one arrives.
    resetRequestIdForNewAttribution();
    // Additional cleanup: if no transaction_id or aff_id in URL or original storage,
    // remove those stale entries following the same logic as tooa condition
    const hasTransactionId = urlParams.has(UrlParam.TRANSACTION_ID) || originalStoredData.aff_click_id;
    const hasAffId = urlParams.has(ParamName.AFF_ID) || originalStoredData.aff_id;
    if (!hasTransactionId && !hasAffId) {
        // Clean up stale entries by keeping them out of the reset data
        // (no additional action needed since storedParamData is already reset)
        if (isDebug) {
            console.log('🧹 Cleaned up stale attribution entries (no transaction_id or aff_id)');
        }
    }
    return { attributionExpired: true, storedParamData: cleanedData };
}
/**
 * Processes URL and stored parameters into paramData.
 * @param paramData - Target parameter data object to populate
 * @param dictionary - Parameter mapping dictionary
 * @param incomingParamData - Parsed URL parameters
 * @param storedParamData - Stored parameter data from localStorage
 */
function processParameters(paramData, dictionary, incomingParamData, storedParamData) {
    // Process all parameters normally - let mergeNewParams() handle aff_id conditional logic
    // Performance: for...in is 30-40% faster than Object.keys().forEach()
    for (const p in dictionary) {
        if (!Object.prototype.hasOwnProperty.call(dictionary, p))
            continue;
        const dictEntry = dictionary[p];
        if (!dictEntry)
            continue;
        const { key } = dictEntry;
        const paramKey = key;
        // Skip lastActivity field as it's handled separately
        if (paramKey === 'lastActivity')
            continue;
        if (incomingParamData[p]) {
            paramData[paramKey] =
                incomingParamData[p];
        }
        else if (storedParamData[paramKey]) {
            const val = storedParamData[paramKey];
            paramData[paramKey] = val;
        }
    }
}
/**
 * Maps a parameter field name to its source URL parameter name.
 * @param field - Field name in TuneParamData
 * @returns Corresponding URL parameter name
 */
function getSourceParamForField(field) {
    if (field === 'aff_sub3')
        return UrlParam.UTM_CAMPAIGN;
    if (field === 'aff_sub4')
        return UrlParam.UTM_CONTENT;
    if (field === 'aff_sub5')
        return UrlParam.UTM_TERM;
    if (field === 'aff_click_id')
        return UrlParam.TRANSACTION_ID;
    return field; // For click IDs and aff_unique5, use same param name
}
/**
 * Clears dependent attribution fields unless they're in incoming URL params.
 * @param paramData - Parameter data to modify
 * @param incomingParamData - URL parameters
 */
function clearDependentFields(paramData, incomingParamData) {
    const dependentFields = [
        'aff_sub3',
        'aff_sub4',
        'aff_sub5',
        'aff_unique5',
        'aff_click_id',
        'gclid',
        'gbraid',
        'wbraid',
        'msclkid',
    ];
    for (let i = 0; i < dependentFields.length; i++) {
        const field = dependentFields[i];
        const sourceParam = getSourceParamForField(field);
        if (!incomingParamData[sourceParam]) {
            delete paramData[field];
        }
    }
}
/**
 * Handles attribution reset when utm_source or utm_medium changes.
 * @param paramData - Parameter data object to modify
 * @param incomingParamData - URL parameters
 * @param storedParamData - Stored parameters
 * @param isDebug - Debug mode flag
 */
function handleAttributionReset(paramData, incomingParamData, storedParamData, isDebug) {
    const incomingUtmSource = incomingParamData[UrlParam.UTM_SOURCE];
    const incomingUtmMedium = incomingParamData[UrlParam.UTM_MEDIUM];
    const storedAffSub = storedParamData.aff_sub;
    const storedAffSub2 = storedParamData.aff_sub2;
    const sourceChanged = incomingUtmSource && storedAffSub && incomingUtmSource !== storedAffSub;
    const mediumChanged = incomingUtmMedium && storedAffSub2 && incomingUtmMedium !== storedAffSub2;
    if (!sourceChanged && !mediumChanged) {
        return;
    }
    clearDependentFields(paramData, incomingParamData);
    if (isDebug) {
        const changedParam = sourceChanged
            ? `utm_source from "${storedAffSub}" to "${incomingUtmSource}"`
            : `utm_medium from "${storedAffSub2}" to "${incomingUtmMedium}"`;
        console.log(`🔄 ${changedParam} - cleared dependent attribution fields`);
    }
}
/**
 * Applies referrer fallback logic when URL parameters are absent.
 * @param paramData - Parameter data object to modify
 * @param utmSourceFromReferrer - Source detected from referrer
 * @param utmMediumFromReferrer - Medium detected from referrer
 * @param attributionExpired - Whether attribution has expired
 * @param storedParamData - Stored parameter data
 * @param incomingParamData - URL parameters
 */
function applyReferrerFallback(paramData, utmSourceFromReferrer, utmMediumFromReferrer, attributionExpired, storedParamData, incomingParamData) {
    // Check and log GTM debug mode (for visibility - does not affect attribution)
    isGTMDebugMode();
    // Priority 1: URL parameters always win when present
    const incomingUtmSource = incomingParamData[UrlParam.UTM_SOURCE];
    const incomingUtmMedium = incomingParamData[UrlParam.UTM_MEDIUM];
    if (incomingUtmSource || incomingUtmMedium) {
        // URL parameters present - they override everything (already handled in processing loop)
        return;
    }
    // Priority 2: No URL parameters - decide whether to use referrer fallback
    // Apply referrer logic if:
    // - Attribution expired + new session, OR
    // - No stored aff_sub2 data exists (first visit or clean slate)
    const shouldApplyReferrerLogic = attributionExpired || !storedParamData.aff_sub2;
    if (shouldApplyReferrerLogic) {
        // Apply referrer-derived medium ('organic' | 'referral' | 'direct')
        paramData.aff_sub2 = utmMediumFromReferrer;
        // Source is set for organic AND referral (null for direct)
        if (utmSourceFromReferrer) {
            paramData.aff_sub = utmSourceFromReferrer;
        }
    }
    // Otherwise preserve existing stored values (already handled in processing loop)
}
/**
 * Handles utm_term special case formatting with network parameter.
 * @param paramData - Parameter data object to modify
 * @param urlParams - URL search parameters
 */
function handleUtmTermSpecialCase(paramData, urlParams) {
    /**
     * utm_term comes from url params and will always be in the format "kwd-{keyword}".
     * This value gets saved off in aff_sub5.
     */
    const keywordString = paramData.aff_sub5 ? `${paramData.aff_sub5}` : '';
    // network is received from the network url param
    const networkString = urlParams.get('network')
        ? `network-${urlParams.get('network')}`
        : '';
    if (keywordString || networkString) {
        // aff_sub5 follows the format of kwd-{keyword}-network-{network}, requested by Nish's team
        paramData.aff_sub5 =
            keywordString && networkString
                ? `${keywordString}-${networkString}`
                : `${keywordString}${networkString}`;
    }
}
/**
 * Applies partner parameter override for IFRAME embeds.
 * When a `partner` URL parameter is present, it overrides aff_sub with the partner value
 * and sets aff_sub2 to 'affiliate'. This takes absolute priority over all other attribution logic.
 *
 * @param paramData - Parameter data object to modify
 * @param urlParams - URL search parameters
 * @param isDebug - Whether debug mode is enabled for logging
 */
function applyPartnerOverride(paramData, urlParams, isDebug) {
    const partnerValue = urlParams.get(UrlParam.PARTNER);
    if (!partnerValue)
        return;
    paramData[ParamName.AFF_SUB] = partnerValue;
    paramData[ParamName.AFF_SUB2] = 'affiliate';
    if (isDebug) {
        console.log(`Partner override applied: aff_sub="${partnerValue}", aff_sub2="affiliate"`);
    }
}
/**
 * Maps incoming URL parameters and referrer data to a structured TuneParamData object.
 *
 * @param [utmSourceFromReferrer, utmMediumFromReferrer] - An array containing UTM source and medium from the referrer.
 * @param isDebug - Whether debug mode is enabled for logging
 * @returns {TuneParamData} - The structured parameter data.
 *
 * @example
 * const utmSourceFromReferrer = 'google';
 * const utmMediumFromReferrer = 'cpc';
 * const paramData = mapParamData([utmSourceFromReferrer, utmMediumFromReferrer], false);
 * // paramData will contain the structured parameter data based on the URL and referrer values.
 */
export function mapParamData([utmSourceFromReferrer, utmMediumFromReferrer], isDebug = false) {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    // Extract geo and zip code data
    const { base64zip, zipCode } = parseGeoAndZipCode();
    // Build parameter mappings and initialize param data
    const dictionary = buildParamMappings(base64zip, zipCode);
    const paramData = initializeParamData(dictionary);
    // Note: VWO integration now lazy-loaded in init() if VWO script detected
    // Note: utmSourceFromReferrer logic now handled conditionally below
    // Only applied when referrer changed or attribution expired
    // Parse incoming URL parameters
    const incomingParamData = parseQuery(queryString.substring(1));
    // Load stored parameter data
    let storedParamData = loadStoredParamData();
    // Handle attribution expiration and cleanup
    const expirationResult = handleAttributionExpiration(storedParamData, urlParams, isDebug);
    const attributionExpired = expirationResult.attributionExpired;
    storedParamData = expirationResult.storedParamData;
    // Process all URL and stored parameters
    processParameters(paramData, dictionary, incomingParamData, storedParamData);
    // Handle attribution reset when source/medium changes
    handleAttributionReset(paramData, incomingParamData, storedParamData, isDebug);
    // Apply referrer fallback logic
    applyReferrerFallback(paramData, utmSourceFromReferrer, utmMediumFromReferrer, attributionExpired, storedParamData, incomingParamData);
    // Handle utm_term special case
    handleUtmTermSpecialCase(paramData, urlParams);
    // Apply partner override for IFRAME embeds (highest priority)
    applyPartnerOverride(paramData, urlParams, isDebug);
    // Performance: use encapsulated utility for object sorting
    return sortObj(paramData);
}
/**
 * Queries the DOM for elements matching the given CSS selector.
 * @param selector - CSS selector string
 * @returns {NodeListOf<HTMLElement>} NodeList of matching elements
 */
export function getElementsFromDom(selector) {
    return document.querySelectorAll(selector);
}
// Note: Link enhancement functions (updateRequestIds, startPeriodicRequestIdUpdate, etc.)
// are now lazy-loaded from src/features/link-enhancement.ts when affiliate links are detected
/**
 * Creates a debug logger that only logs when debug mode is enabled
 *
 * @param isDebug - Whether debug mode is enabled
 * @returns Debug logging function
 */
function createDebugLogger(isDebug) {
    return (...args) => {
        if (isDebug) {
            console.log(...args);
        }
    };
}
/**
 * Logs build target information for debugging
 *
 * @param debugLog - Debug logging function
 */
function logBuildTarget(debugLog) {
    // These variables are injected by webpack DefinePlugin during build
    const buildTarget = typeof BUILD_TARGET !== 'undefined' ? BUILD_TARGET : 'unknown';
    const isModern = typeof BUILD_MODERN !== 'undefined' ? BUILD_MODERN : false;
    debugLog(`🎯 WP Attribution Library - Build Target: ${buildTarget} (modern: ${isModern})`);
}
/**
 * Events that signal the user is about to act, used to warm deferred chunks
 * before the click that depends on them.
 */
const FIRST_INTERACTION_EVENTS = [
    'pointerdown',
    'touchstart',
    'keydown',
];
/**
 * Lazy-loads a module using requestIdleCallback or setTimeout fallback
 *
 * @param loader - Function to load the module
 * @param timeout - Timeout in milliseconds (default: 3000)
 */
function deferModuleLoad(loader, timeout = 3000) {
    let started = false;
    const start = () => {
        if (started)
            return;
        started = true;
        for (const type of FIRST_INTERACTION_EVENTS) {
            document.removeEventListener(type, start, true);
        }
        loader();
    };
    // Warm on first interaction. On a single-CTA landing page a tap can land
    // seconds before the idle callback, and the chunk owns work the click
    // handler depends on, so waiting for idle alone loses the race.
    for (const type of FIRST_INTERACTION_EVENTS) {
        document.addEventListener(type, start, { capture: true, passive: true });
    }
    if ('requestIdleCallback' in window) {
        window.requestIdleCallback(start, { timeout });
    }
    else {
        setTimeout(start, timeout);
    }
}
/**
 * Handles lazy-loading of the position-tracking chunk when card position
 * attributes are present on the page
 *
 * @param tuneLinks - NodeList of affiliate links passed through to the module
 * @param debugLog - Debug logging function
 */
function handlePositionTracking(tuneLinks, debugLog) {
    if (!document.querySelector('[data-global-position]')) {
        debugLog('⏭️ No data-global-position detected, skipping position-tracking chunk');
        return;
    }
    debugLog('📦 Scheduling position-tracking chunk load (data-global-position detected)');
    const loadPositionTracking = () => {
        debugLog('⬇️ Loading position-tracking chunk...');
        void import(
        /* webpackChunkName: "position-tracking" */ './features/position-tracking')
            .then(module => {
            debugLog('✅ Position tracking chunk loaded successfully');
            module.setupPositionTracking(tuneLinks);
        })
            .catch(error => {
            console.warn('❌ Failed to load position-tracking chunk:', error);
        });
    };
    deferModuleLoad(loadPositionTracking);
}
/**
 * Handles link enhancement for affiliate links
 *
 * @param tuneLinks - NodeList or Array of affiliate links to enhance
 * @param params - Attribution parameters to append
 * @param debugLog - Debug logging function
 */
function handleLinkEnhancement(tuneLinks, params, debugLog) {
    if (!tuneLinks || tuneLinks.length === 0) {
        debugLog('⏭️ No affiliate links detected, skipping link-enhancement chunk');
        return;
    }
    // Process links immediately to preserve hardcoded parameters
    for (let i = 0, len = tuneLinks.length; i < len; i++) {
        appendParams(tuneLinks[i], params);
    }
    debugLog(`📦 Scheduling link-enhancement chunk load (${tuneLinks.length} affiliate links detected)`);
    // Lazy-load link enhancement module
    const loadLinkEnhancement = () => {
        debugLog('⬇️ Loading link-enhancement chunk...');
        void import(
        /* webpackChunkName: "link-enhancement" */ './features/link-enhancement')
            .then(module => {
            debugLog('✅ Link enhancement chunk loaded successfully');
            void module.updateRequestIds(tuneLinks);
        })
            .catch(error => {
            console.warn('❌ Failed to load link enhancement chunk:', error);
        });
    };
    deferModuleLoad(loadLinkEnhancement);
    handlePositionTracking(tuneLinks, debugLog);
}
/**
 * Cleans up transient parameters that shouldn't be stored
 *
 * @param params - Attribution parameters to clean
 */
function cleanTransientParams(params) {
    delete params.source;
    delete params.ZipCode;
    delete params.aff_unique2;
    delete params.aff_unique3;
    delete params.aff_unique4;
}
/**
 * Gets currently stored attribution data from localStorage
 *
 * @returns Parsed attribution data or empty object if not found/invalid
 */
function getStoredAttributionData() {
    const currentStoredDataString = getItemFromLocalStorage(getStorageKey);
    if (!currentStoredDataString) {
        return {};
    }
    try {
        return JSON.parse(currentStoredDataString);
    }
    catch (_error) {
        return {};
    }
}
/**
 * Saves attribution data to localStorage (immediately or debounced)
 *
 * @param params - Attribution parameters to save
 * @param currentStoredData - Currently stored data for comparison
 * @param debugLog - Debug logging function
 * @param getStorageManager - Function to get storage manager for debounced writes
 */
function saveAttributionData(params, currentStoredData, debugLog, getStorageManager) {
    var _a;
    const hasChanged = hasParametersChanged(params, currentStoredData);
    // Stamp the touch clock only on a real attribution change; otherwise carry forward
    // the stored value so plain referrer-less pageviews do NOT slide the 30-day cap.
    // Grandfather: pre-feature records have no timestamp — start their clock at first
    // save (?? Date.now()) so the cap can eventually engage; this is a no-op for
    // already-stamped records (their existing value is carried forward, still frozen).
    params.lastAttributionTouch = hasChanged
        ? Date.now()
        : ((_a = currentStoredData.lastAttributionTouch) !== null && _a !== void 0 ? _a : Date.now());
    // Carry the request id forward. `params` is rebuilt from the URL and never
    // carries it, so writing params wholesale would drop the id the click
    // pipeline reads — and the id may already be on links or in a click that
    // navigated. Applies to MapiJS, fallback and provisional ids alike.
    if (currentStoredData.requestId && !params.requestId) {
        params.requestId = currentStoredData.requestId;
    }
    // Carry the quarantine marker for the same reason: params is rebuilt from the
    // URL, so writing it wholesale would drop the marker and let the pre-reset
    // MapiJS id become preferred again on the very next save.
    if (currentStoredData.staleMapiRequestId && !params.staleMapiRequestId) {
        params.staleMapiRequestId = currentStoredData.staleMapiRequestId;
    }
    if (hasChanged) {
        debugLog('📝 Attribution parameters changed, updating localStorage immediately:', params);
        setItemInLocalStorage(getStorageKey, JSON.stringify(params));
    }
    else {
        debugLog('✅ Attribution parameters unchanged, scheduling debounced activity timestamp update');
        void getStorageManager().then(({ storageManager }) => {
            storageManager.debouncedWrite(getStorageKey, JSON.stringify(params), 1000);
        });
    }
    debugLog('💾 Saved to localStorage:', JSON.parse(getItemFromLocalStorage(getStorageKey)));
}
/**
 * Synchronously scans window.dataLayer for any VWO variant event already
 * pushed, applying it to params.aff_unique2. Lets link enhancement (which
 * runs before the async vwo-integration chunk resolves) include the variant
 * on re-init, e.g. when addTuneParams() is invoked after a late push.
 */
function applyPendingVWOVariant(params) {
    if (params.aff_unique2)
        return;
    const dataLayer = window.dataLayer;
    if (!Array.isArray(dataLayer))
        return;
    const vwoEvent = dataLayer.find((e) => typeof e === 'object' &&
        e !== null &&
        e.event === 'vwo-data-push-ga4' &&
        e.vwo_exp_variant_string !==
            undefined);
    if (vwoEvent) {
        params.aff_unique2 = vwoEvent.vwo_exp_variant_string;
    }
}
function handleVWOIntegration(params, debugLog) {
    var _a;
    // Only load the chunk when VWO is configured for this page. The signal is the
    // inline `clwpScriptData.vwo` block emitted in <head> (present pre-consent),
    // NOT a DOM script element — the third-party VWO script is loaded by GTM via
    // the `load_js_vwo` event and may arrive after this init under consent
    // management. Gating here skips the chunk fetch + dataLayer push-wrapper on
    // pages that never run VWO, while still arming the listener before VWO's
    // variant push on pages that do.
    if (!((_a = window.clwpScriptData) === null || _a === void 0 ? void 0 : _a.vwo)) {
        debugLog('⏭️ No clwpScriptData.vwo signal detected, skipping vwo-integration chunk');
        return;
    }
    debugLog('📦 Scheduling vwo-integration chunk load (clwpScriptData.vwo signal detected)');
    const loadVWOIntegration = () => {
        debugLog('⬇️ Loading vwo-integration chunk...');
        void import(
        /* webpackChunkName: "vwo-integration" */ './features/vwo-integration')
            .then(module => {
            debugLog('✅ VWO integration chunk loaded successfully');
            module.setupVWOIntegration(params);
        })
            .catch(error => {
            console.warn('❌ Failed to load VWO integration chunk:', error);
        });
    };
    deferModuleLoad(loadVWOIntegration);
}
/**
 * Main initialization function that captures attribution parameters and enhances affiliate links.
 *
 * This function performs the following operations:
 * - Detects and processes UTM parameters from URL and referrer
 * - Stores attribution data in localStorage for persistence
 * - Finds and enhances affiliate links (go.*, au.*) with attribution parameters
 * - Integrates with MapiJS for session management
 * - Sets up VWO integration if available
 * - Implements deferred initialization for optimal Core Web Vitals
 *
 * The function is designed to be called once per page load and handles all
 * attribution tracking initialization automatically.
 *
 * @example
 * // Called automatically when script loads:
 * TuneModule.init();
 */
export function init() {
    // Lazy-load storage manager for optimized localStorage writes
    let storageManagerPromise = null;
    const getStorageManager = () => {
        if (!storageManagerPromise) {
            storageManagerPromise = import(
            /* webpackChunkName: "storage-manager" */ './utils/storage-manager');
        }
        return storageManagerPromise;
    };
    // Setup debug logging
    const urlParams = new URLSearchParams(window.location.search);
    const isDebug = urlParams.get('debug') === '1' || urlParams.get('debug') === '2';
    const debugLog = createDebugLogger(isDebug);
    debugLog(`📦 WP Attribution Library runtime initialized`);
    if (isDebug) {
        logBuildTarget(debugLog);
    }
    // Process attribution parameters
    const paramsFromReferrer = setUTMFromReferrer();
    const params = mapParamData(paramsFromReferrer, isDebug);
    const tuneLinks = getTuneLinks();
    debugLog(`🔍 Checking for affiliate links (go.*, au.*): ${tuneLinks ? tuneLinks.length : 0} found`);
    // Sync VWO dataLayer scan before link enhancement so any already-pushed
    // variant is included in this init's link rewrite. The async chunk below
    // still attaches the listener for late pushes; both paths are needed
    // because addTuneParams() re-runs init() and we must not enhance links
    // before the variant is read.
    applyPendingVWOVariant(params);
    // Bot-confidence sentinel: mark load-time enhanced hrefs as "no click yet".
    // A real click overwrites this with the computed verdict; cleanTransientParams
    // strips it before storage (aff_unique4 stays transient).
    params.aff_unique4 = BOT_SIGNAL.PAGE_LOAD_SENTINEL;
    // Handle link enhancement
    handleLinkEnhancement(tuneLinks, params, debugLog);
    // Prepare attribution data for storage
    const currentStoredData = getStoredAttributionData();
    cleanTransientParams(params);
    params.lastActivity = Date.now();
    // Save attribution data
    saveAttributionData(params, currentStoredData, debugLog, getStorageManager);
    // Handle VWO integration
    handleVWOIntegration(params, debugLog);
    // Setup fallback
    window.addTuneParams = () => {
        void init();
    };
    // Last statement in init() on purpose: telemetry reports this flag to
    // distinguish a fault before load-time enhancement from one after, so it has
    // to mean init actually finished, not "nearly".
    initCompleted = true;
}
// ===============================================
// BOT-CONFIDENCE SIGNAL (aff_unique4)
// ===============================================
/**
 * Tunable thresholds and the page-load sentinel for the bot-confidence signal.
 * Kept as a local const object (not build-time CONFIG) so the logic is
 * self-contained and unit-testable.
 */
const BOT_SIGNAL = {
    /** Written on affiliate hrefs at page load; overwritten by a real click. */
    PAGE_LOAD_SENTINEL: 'p',
    /** Trusted click sooner than this (ms since arm) with no engagement = suspicious. */
    FAST_CLICK_MS: 400,
    /** Dwell (ms since arm) at/above which the 'e' (elapsed) flag is set. */
    MIN_DWELL_MS: 1000,
    /** Untrusted clicks multiply their raw score by this (hard trust gate). */
    UNTRUSTED_MULTIPLIER: 0.25,
    /** Each flag's share of the human-confidence pie (sums to 100). */
    WEIGHTS: { t: 40, m: 25, s: 15, e: 12, k: 8 },
};
const engagement = {
    moved: false,
    scrolled: false,
    keyed: false,
    armedAt: 0,
};
/**
 * Arms one-shot passive listeners that flip engagement flags on the first
 * interaction of each kind, then self-remove. ~zero CWV cost: no work
 * after the first event of each type. touchstart counts as movement so fast
 * mobile taps still score human.
 *
 * @param state - The engagement state to populate
 */
export function recordEngagement(state) {
    state.armedAt = Date.now();
    const opts = {
        passive: true,
        capture: true,
        once: true,
    };
    const onMove = () => {
        state.moved = true;
    };
    document.addEventListener('pointermove', onMove, opts);
    document.addEventListener('mousemove', onMove, opts);
    document.addEventListener('touchstart', onMove, opts);
    document.addEventListener('scroll', () => {
        state.scrolled = true;
    }, opts);
    document.addEventListener('keydown', () => {
        state.keyed = true;
    }, opts);
}
/**
 * Computes the aff_unique4 bot-confidence token from the click event's trust,
 * observed engagement, and dwell. Pure — no DOM, no side effects.
 *
 * Format `<verdict>:<flags>:<score>`: verdict h/s/b; flags (order tmske)
 * t(trusted) m(moved) s(scrolled) k(keyed) e(plausible dwell); score 0-100 =
 * weighted sum of present flags, ×0.25 when untrusted (hard trust gate). Verdict
 * biased so real humans score `h`.
 *
 * @param isTrusted - event.isTrusted of the click
 * @param state - Engagement state for this pageview
 * @param now - Current timestamp (Date.now) at click
 * @returns The verdict:flags:score string
 */
export function computeBotVerdict(isTrusted, state, now) {
    // armedAt 0 (recorder never armed) => dwell 0, not a huge value.
    const dwell = state.armedAt > 0 ? now - state.armedAt : 0;
    const w = BOT_SIGNAL.WEIGHTS;
    const present = [
        ['t', isTrusted, w.t],
        ['m', state.moved, w.m],
        ['s', state.scrolled, w.s],
        ['k', state.keyed, w.k],
        ['e', dwell >= BOT_SIGNAL.MIN_DWELL_MS, w.e],
    ];
    let flags = '';
    let raw = 0;
    for (const [char, on, weight] of present) {
        if (on) {
            flags += char;
            raw += weight;
        }
    }
    const score = Math.round(isTrusted ? raw : raw * BOT_SIGNAL.UNTRUSTED_MULTIPLIER);
    let verdict;
    if (!isTrusted) {
        verdict = 'b';
    }
    else if (state.moved || state.scrolled || state.keyed) {
        verdict = 'h';
    }
    else {
        // Trusted but no engagement: too-fast = suspicious, otherwise soft-human.
        verdict = dwell < BOT_SIGNAL.FAST_CLICK_MS ? 's' : 'h';
    }
    return `${verdict}:${flags}:${score}`;
}
const affiliateClickHandlers = [];
/**
 * Registers a click behavior into the central affiliate-click pipeline. This is
 * the extensibility seam: features (VWO today; position tracking, mapi
 * request-id refresh, etc. later) opt into click-time behavior here.
 *
 * @param handler - Behavior to run on affiliate click, after base enhancement
 */
export function registerAffiliateClickHandler(handler) {
    affiliateClickHandlers.push(handler);
}
/**
 * Synchronously derives attribution params the same way init() does before
 * link enhancement: referrer/URL mapping plus any VWO variant already present
 * in the dataLayer. Used at click time so a click before (or independent of)
 * init still lands the current params — including aff_unique2.
 *
 * @returns Freshly derived attribution params
 */
function computeClickParams() {
    const params = mapParamData(setUTMFromReferrer());
    applyPendingVWOVariant(params);
    return params;
}
/**
 * Whether init() has finished. Reported with client errors: a fault before init
 * completes means load-time link enhancement never ran, which is a materially
 * different failure from one after.
 */
let initCompleted = false;
let clickPipelineArmed = false;
/** 0 = off, 1 = log+continue, 2 = log+block. Read at arm time (pre-init). */
let botDebugLevel = 0;
/**
 * Reads the last-known request ID synchronously: the real MapiJS id from the
 * `mapi` storage, else the session-sticky fallback persisted in cl_param_data.
 * Read-only — never generates or writes an id (the link-enhancement chunk owns
 * that). Lets the click pipeline land aff_unique1/cl_system_id/clreqid even
 * before the async chunk resolves.
 *
 * @returns The request ID if one is stored, otherwise undefined
 */
function readStoredRequestId() {
    var _a;
    const stored = getStoredAttributionData();
    const mapiRequestId = readMapiRequestId();
    // Prefer the live MapiJS id, unless it is the exact id quarantined at the
    // last attribution reset. `mapi` is MapiJS's own key and localStorage-backed,
    // so on a long-dormant browser it still holds the previous session's id until
    // MapiJS loads and refreshes it. Preferring it unconditionally resurrected a
    // pre-reset id past both expiry gates. Skipping only the recorded value
    // self-neutralises: the moment MapiJS mints a new id it differs and is
    // preferred again, and we never write into MapiJS's key.
    if (mapiRequestId && mapiRequestId !== stored.staleMapiRequestId) {
        return mapiRequestId;
    }
    return (_a = stored.requestId) !== null && _a !== void 0 ? _a : undefined;
}
/**
 * Reads the request id MapiJS has persisted under its own `mapi` storage key.
 *
 * @returns The MapiJS request ID if one is stored, otherwise undefined
 */
function readMapiRequestId() {
    try {
        const mapi = getItemFromLocalStorage('mapi');
        if (mapi) {
            const parsed = JSON.parse(mapi);
            if (parsed.requestId)
                return parsed.requestId;
        }
    }
    catch (_e) {
        // Ignore malformed mapi storage.
    }
    return undefined;
}
/**
 * Prefix marking a request id minted synchronously by the core bundle before
 * MapiJS has produced one. Deliberately distinct from the `fallback-` prefix:
 * the link-enhancement chunk treats `fallback-` as final, but must still
 * prefer a real MapiJS id over a provisional one.
 */
export const PROVISIONAL_REQUEST_ID_PREFIX = 'provisional-';
/**
 * Generates a UUID v4, preferring the platform implementation.
 *
 * @returns A UUID v4 string
 */
function generateUuidV4() {
    const cryptoObj = window.crypto;
    if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
        return cryptoObj.randomUUID();
    }
    // randomUUID needs Chrome 92+, but getRandomValues goes back much further —
    // and the traffic that most often lands here is older Android WebViews. This
    // id becomes clreqid/cl_system_id/aff_unique1, a join key downstream, so a
    // collision would merge two sessions' attribution. Prefer real entropy.
    if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
        const bytes = new Uint8Array(16);
        cryptoObj.getRandomValues(bytes);
        bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
        bytes[8] = (bytes[8] & 0x3f) | 0x80; // RFC 4122 variant
        const hex = [];
        for (let i = 0; i < bytes.length; i++) {
            hex.push(bytes[i].toString(16).padStart(2, '0'));
        }
        const h = hex.join('');
        return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
    }
    // Last resort only: no crypto at all.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}
/**
 * Guarantees a readable request id exists before any click can occur.
 *
 * Request-id ownership otherwise sits in the deferred link-enhancement chunk,
 * which cannot resolve before a fast tap: short-dwell traffic (paid native ads
 * landing in an in-app WebView on a single-CTA page) navigated with
 * aff_unique1/cl_system_id/clreqid entirely absent, so Tune recorded the click
 * unattributed. Runs synchronously at script load.
 *
 * Prefers an existing MapiJS id, then any id already stored, and only mints a
 * provisional id when neither exists. The chunk later upgrades links to the
 * real MapiJS id if one arrives.
 *
 * @returns The request id now available to the click pipeline, or undefined if
 *   storage is unavailable
 */
export function ensureRequestId() {
    try {
        const existing = readStoredRequestId();
        if (existing)
            return existing;
        const provisional = `${PROVISIONAL_REQUEST_ID_PREFIX}${Date.now()}-${generateUuidV4()}`;
        const storedData = getStoredAttributionData();
        storedData.requestId = provisional;
        setItemInLocalStorage(getStorageKey, JSON.stringify(storedData));
        return provisional;
    }
    catch (_e) {
        // Storage unavailable — degrade to the previous behaviour rather than throw
        // on the path that arms navigation.
        reportClientError('requestId', _e, { initCompleted });
        return undefined;
    }
}
/**
 * Writes the request id into the three Tune request-id params of a URL.
 *
 * The single encoding authority for request-id writes: both the synchronous
 * click path and the link-enhancement chunk go through here, so the two can no
 * longer disagree about how params are escaped.
 *
 * @param href - The current href
 * @param requestId - The request ID to apply
 * @returns The rewritten href with params sorted, or null if href is not a URL
 */
export function setRequestIdOnUrl(href, requestId) {
    try {
        const url = new URL(href);
        for (const param of REQUEST_ID_KEYS) {
            url.searchParams.set(param, requestId);
        }
        // Alphabetical order, matching replaceQueryParameters on the append path.
        const sorted = Array.from(url.searchParams.entries()).sort(([a], [b]) => a.localeCompare(b));
        const sortedParams = new URLSearchParams();
        for (const [key, value] of sorted) {
            sortedParams.append(key, value);
        }
        url.search = sortedParams.toString();
        // url.toString() re-encodes searchParams losslessly (new URL() already
        // decoded them). Do NOT decodeURIComponent the whole URL: that unwinds
        // %26/%3D/%2B in OTHER param values (e.g. an aff_sub containing &/= or the
        // base64 hardcoded_values), truncating or injecting params server-side.
        return url.toString();
    }
    catch (_e) {
        return null;
    }
}
/**
 * Writes the request ID into the three Tune request-id params on an anchor.
 *
 * @param anchor - The affiliate anchor to update
 * @param requestId - The request ID to write
 */
function applyStoredRequestId(anchor, requestId) {
    const next = setRequestIdOnUrl(anchor.href, requestId);
    // Invalid URL — leave href unchanged.
    if (next !== null)
        anchor.href = next;
}
/**
 * Central handler for affiliate-link activation (click, capture phase). Runs
 * base enhancement (params + request id) synchronously before navigation, then
 * any registered feature handlers. Listening on click alone is sufficient: the
 * capture-phase handler runs before the browser navigates, and every feature
 * writes at click time (no mousedown head start needed). debug=2 blocks
 * navigation via preventDefault. Never throws into navigation.
 *
 * @param event - The click event
 */
function handleAffiliateActivation(event) {
    const target = event.target;
    if (!(target instanceof Element))
        return;
    const anchor = target.closest(AFFILIATE_ANCHOR_SELECTOR);
    if (!anchor)
        return;
    if (botDebugLevel >= 2)
        event.preventDefault();
    try {
        const params = computeClickParams();
        const verdict = computeBotVerdict(event.isTrusted, engagement, Date.now());
        params.aff_unique4 = verdict;
        appendParams(anchor, params);
        const requestId = readStoredRequestId();
        if (requestId)
            applyStoredRequestId(anchor, requestId);
        if (botDebugLevel > 0) {
            console.log('🔎 cl-attr affiliate click preview', {
                wouldNavigateTo: anchor.href,
                aff_unique4: verdict,
                blocked: botDebugLevel >= 2,
            });
        }
        for (const handler of affiliateClickHandlers) {
            try {
                handler(anchor, params);
            }
            catch (_e) {
                // A feature handler must never block navigation.
            }
        }
    }
    catch (_e) {
        // Fall through to native navigation on any failure — but report it. This
        // catch swallowing silently is why a real production failure went unseen:
        // clicks reached Tune with no attribution params and left no trace here.
        reportClientError('click', _e, {
            href: anchor.getAttribute('href'),
            initCompleted,
        });
    }
}
/**
 * Arms the affiliate-click pipeline. Registered eagerly at script load,
 * independent of the deferred init(), so a click before init still enhances
 * the link. Idempotent. The listener body does nothing until an affiliate
 * anchor is actually clicked, so non-affiliate pages pay ~zero.
 */
export function armClickPipeline() {
    if (clickPipelineArmed)
        return;
    clickPipelineArmed = true;
    const debug = new URLSearchParams(window.location.search).get('debug');
    botDebugLevel = debug === '2' ? 2 : debug === '1' ? 1 : 0;
    recordEngagement(engagement);
    // Mint the request id before the listener is live: handleAffiliateActivation
    // only reads it, and the chunk that used to own minting resolves too late
    // for a fast tap.
    ensureRequestId();
    document.addEventListener('click', handleAffiliateActivation, true);
}
// ===============================================
// ASSEMBLE MODULE FOR UMD EXPORT
// ===============================================
/**
 * The main ClAttr object providing access to all attribution tracking functionality.
 *
 * This module provides a comprehensive API for tracking marketing attribution parameters,
 * managing affiliate links, and integrating with external systems like MapiJS and VWO.
 *
 * **Core Functions:**
 * - `init()` - Initializes attribution tracking and link enhancement
 * - `mapParamData()` - Processes URL parameters and referrer data
 * - `isAttributionExpired()` - Checks if attribution data has expired
 * - `hasExternalReferral()` - Detects whether the pageview arrived via an external referrer
 *
 * **Utility Functions:**
 * - Query string parsing and building
 * - URL parameter manipulation
 * - Storage operations (localStorage/cookies)
 * - Base64 encoding for special characters
 *
 * **Integration:**
 * - MapiJS session management
 * - VWO A/B testing integration
 * - Google Click IDs (gclid) and other platform identifiers
 *
 * @example
 * // Basic usage (automatic initialization):
 * <script src="https://attr.example.com/latest/cl-attr.min.js"></script>
 *
 * @example
 * // Manual initialization:
 * ClAttr.init();
 *
 * @example
 * // Check attribution expiration:
 * const storedData = JSON.parse(localStorage.getItem('cl_param_data'));
 * if (ClAttr.isAttributionExpired(storedData)) {
 *   console.log('Attribution has expired');
 * }
 */
export const ClAttr = {
    // Methods
    isAttributionExpired,
    isAttributionCapExpired,
    isGTMDebugMode,
    hasExternalReferral,
    hasParametersChanged,
    sortObj,
    getTuneLinks,
    parseQuery,
    buildQuery,
    mergeNewParams,
    replaceQueryParameters,
    isObjectEmpty,
    sanitizeInput,
    validateParameterLength,
    validateZipCode,
    getItemFromLocalStorage,
    getItemFromCookieStorage,
    setItemInLocalStorage,
    appendParams,
    extractReferrerHostname,
    setUTMFromReferrer,
    getFBCookies,
    toBase64Unicode,
    capitalize,
    mapParamData,
    computeBotVerdict,
    getElementsFromDom,
    init,
    armClickPipeline,
    registerAffiliateClickHandler,
    // Constants
    getStorageKey,
    getAffilliateDomain,
    getOrganicSources,
};
// Core Web Vitals optimization: Defer initialization until after FCP
function scheduleInit() {
    const runInit = () => {
        void init();
    };
    // Use requestIdleCallback to run after browser is idle (after FCP)
    if ('requestIdleCallback' in window) {
        window.requestIdleCallback(runInit, { timeout: 2000 }); // Max 2 second wait
    }
    else {
        // Fallback: use setTimeout to defer until next tick
        setTimeout(runInit, 0);
    }
}
// Arm the click pipeline eagerly (independent of the deferred init) so a click
// before init still enhances the affiliate link — the timing-independent
// failsafe for landing aff_unique2 on Tune links.
armClickPipeline();
// Schedule initialization based on document state
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleInit);
}
else {
    scheduleInit();
}
